import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Pin, AlertTriangle, Calendar, User, Users, Pencil, Archive, Save, X, Trash2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Narrow whitelist matching backend `AnnouncementUpdate` model in
// /app/backend/models_step6.py. Anything outside this set is intentionally
// out-of-scope for Phase A (audience/scheduling/flyer edits are Phase B).
const EDITABLE_PRIORITIES = ['normal', 'important', 'urgent'];

const AnnouncementDetailPage = () => {
  const { announcementId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);

  // Phase A: in-place edit state. Admin-only UI; backend already enforces
  // the same role gate on PUT, so a leaked render is safe.
  const isAdmin = user?.role === 'league_admin' || user?.role === 'super_admin';
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    fetchAnnouncement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementId]);

  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/announcements/${announcementId}`,
        { withCredentials: true }
      );
      setAnnouncement(response.data);
    } catch (error) {
      console.error('Failed to fetch announcement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red text-white';
      case 'important': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const beginEdit = () => {
    if (!announcement) return;
    setActionError('');
    setEditForm({
      title: announcement.title || '',
      body: announcement.body || '',
      priority: announcement.priority || 'normal',
      is_pinned: !!announcement.is_pinned,
      is_critical: !!announcement.is_critical,
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditForm(null);
    setActionError('');
  };

  const buildDiff = () => {
    if (!announcement || !editForm) return {};
    const diff = {};
    if (editForm.title !== announcement.title) diff.title = editForm.title;
    if (editForm.body !== announcement.body) diff.body = editForm.body;
    if (editForm.priority !== announcement.priority) diff.priority = editForm.priority;
    if (editForm.is_pinned !== !!announcement.is_pinned) diff.is_pinned = editForm.is_pinned;
    if (editForm.is_critical !== !!announcement.is_critical) diff.is_critical = editForm.is_critical;
    return diff;
  };

  const saveEdit = async () => {
    if (!editForm) return;
    const title = (editForm.title || '').trim();
    const body = (editForm.body || '').trim();
    if (!title || !body) {
      setActionError('Title and body cannot be empty.');
      return;
    }
    const diff = buildDiff();
    diff.title = title;
    diff.body = body;
    setSaving(true);
    setActionError('');
    try {
      await axios.put(
        `${API_URL}/api/announcements/${announcementId}`,
        diff,
        { withCredentials: true }
      );
      // Re-fetch to pick up updated_at + any server-side normalization.
      await fetchAnnouncement();
      setEditMode(false);
      setEditForm(null);
    } catch (error) {
      console.error('Failed to update announcement:', error);
      setActionError(error.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const archiveAnnouncement = async () => {
    if (!announcement) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Archive "${announcement.title}"?\n\n` +
      'It will be hidden from the inbox. This does not delete the record.'
    );
    if (!ok) return;
    setArchiving(true);
    setActionError('');
    try {
      await axios.put(
        `${API_URL}/api/announcements/${announcementId}`,
        { archived: true },
        { withCredentials: true }
      );
      navigate('/communications?tab=messages');
    } catch (error) {
      console.error('Failed to archive announcement:', error);
      setActionError(error.response?.data?.detail || 'Failed to archive announcement.');
      setArchiving(false);
    }
  };

  const deleteAnnouncement = async () => {
    if (!announcement) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      'This permanently deletes the announcement and cannot be undone. Continue?'
    );
    if (!ok) return;
    setDeleting(true);
    setActionError('');
    try {
      await axios.delete(
        `${API_URL}/api/announcements/${announcementId}`,
        { withCredentials: true }
      );
      navigate('/communications?tab=messages');
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      setActionError(error.response?.data?.detail || 'Failed to delete announcement.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  if (!announcement) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Announcement not found</p>
          <Button onClick={() => navigate('/communications?tab=messages')} className="mt-4">
            Back to Communications
          </Button>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="announcement-detail">
        {/* Back Button */}
        <button
          onClick={() => navigate('/communications?tab=messages')}
          className="flex items-center text-gray-600 hover:text-navy mb-6"
          data-testid="announcement-back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Communications
        </button>

        <Card>
          <CardContent className="p-6 sm:p-8">
            {/* Admin action bar (Phase A) */}
            {isAdmin && !editMode && (
              <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={beginEdit}
                  data-testid="announcement-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red border-red hover:bg-red/10"
                  onClick={archiveAnnouncement}
                  disabled={archiving || deleting}
                  data-testid="announcement-archive-btn"
                >
                  <Archive className="w-4 h-4 mr-1" />
                  {archiving ? 'Archiving...' : 'Archive'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white bg-red border-red hover:bg-red/90"
                  onClick={deleteAnnouncement}
                  disabled={archiving || deleting}
                  data-testid="announcement-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {deleting ? 'Deleting...' : 'Delete permanently'}
                </Button>
              </div>
            )}

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {(editMode ? editForm.is_pinned : announcement.is_pinned) && (
                <Badge className="bg-gold text-navy">
                  <Pin className="w-3 h-3 mr-1" />
                  Pinned
                </Badge>
              )}
              {(editMode ? editForm.is_critical : announcement.is_critical) && (
                <Badge className="bg-red text-white">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Critical
                </Badge>
              )}
              <Badge className={getPriorityColor(editMode ? editForm.priority : announcement.priority)}>
                {editMode ? editForm.priority : announcement.priority}
              </Badge>
              {announcement.team_name && (
                <Badge variant="outline">{announcement.team_name}</Badge>
              )}
              {announcement.org_wide && (
                <Badge variant="outline" className="border-navy text-navy">
                  Organization-Wide
                </Badge>
              )}
              {announcement.comm_type === 'event_reminder' && (
                <Badge
                  variant="outline"
                  className="border-navy text-navy"
                  data-testid="announcement-event-reminder-badge"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  Event Reminder
                </Badge>
              )}
            </div>

            {/* Title — read or edit */}
            {editMode ? (
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Title</label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="text-lg"
                  data-testid="announcement-edit-title"
                />
              </div>
            ) : (
              <h1 className="text-2xl sm:text-3xl font-bold text-navy mb-4">
                {announcement.title}
              </h1>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6 pb-6 border-b">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {announcement.sender_name || 'League Admin'}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {format(parseISO(announcement.published_at || announcement.created_at), 'MMMM d, yyyy \'at\' h:mm a')}
              </div>
              {announcement.recipient_count > 0 && (
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Sent to {announcement.recipient_count} recipients
                </div>
              )}
              {announcement.event_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/events/${announcement.event_id}`)}
                  data-testid="announcement-view-event-link"
                  className="flex items-center text-navy hover:underline"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  View Event
                </button>
              )}
            </div>

            {/* Body — read or edit */}
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Message</label>
                  <Textarea
                    value={editForm.body}
                    onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                    rows={8}
                    data-testid="announcement-edit-body"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Priority</label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="announcement-edit-priority"
                    >
                      {EDITABLE_PRIORITIES.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 mt-5 sm:mt-6">
                    <input
                      type="checkbox"
                      checked={editForm.is_pinned}
                      onChange={(e) => setEditForm({ ...editForm, is_pinned: e.target.checked })}
                      data-testid="announcement-edit-pinned"
                    />
                    Pin to top
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 mt-5 sm:mt-6">
                    <input
                      type="checkbox"
                      checked={editForm.is_critical}
                      onChange={(e) => setEditForm({ ...editForm, is_critical: e.target.checked })}
                      data-testid="announcement-edit-critical"
                    />
                    Mark as critical
                  </label>
                </div>

                {actionError && (
                  <p
                    className="text-sm text-red"
                    data-testid="announcement-edit-error"
                  >
                    {actionError}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <Button
                    onClick={saveEdit}
                    disabled={saving}
                    data-testid="announcement-edit-save"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? 'Saving...' : 'Save changes'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={cancelEdit}
                    disabled={saving}
                    data-testid="announcement-edit-cancel"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="prose prose-navy max-w-none">
                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {announcement.body}
                </p>
              </div>
            )}

            {/* Inline error for archive when not in edit mode */}
            {!editMode && actionError && (
              <p
                className="text-sm text-red mt-4"
                data-testid="announcement-action-error"
              >
                {actionError}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Footer actions */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/communications?tab=messages')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    </ParentLayout>
  );
};

export default AnnouncementDetailPage;
